import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { CheckCircle2, Copy, CreditCard, Eye, EyeOff, KeyRound, Save, ShieldCheck, Webhook } from 'lucide-react';
import { db, firebaseConfig } from '../../lib/firebase';
import { useToast } from '../../context/ToastContext';
import PageHeader from '../../components/PageHeader';
import Card from '../../components/Card';
import Badge from '../../components/Badge';

const defaultForm = {
  provider: 'razorpay',
  enabled: false,
  mode: 'test',
  merchantName: 'Weilink Signage',
  keyId: '',
  keySecret: '',
  webhookSecret: '',
};

export default function PaymentGatewaysPage() {
  const { notify } = useToast();
  const [form, setForm] = useState(defaultForm);
  const [configured, setConfigured] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);

  const webhookUrl = useMemo(
    () => `https://us-central1-${firebaseConfig.projectId}.cloudfunctions.net/razorpayWebhook`,
    []
  );

  useEffect(() => {
    getDoc(doc(db, 'system_settings', 'payment_gateway')).then(snap => {
      if (!snap.exists()) return;
      const data = snap.data();
      setConfigured(Boolean(data.configured));
      setForm(prev => ({
        ...prev,
        provider: data.provider || 'razorpay',
        enabled: data.enabled === true,
        mode: data.mode || 'test',
        merchantName: data.merchantName || 'Weilink Signage',
        keyId: data.keyId || '',
      }));
    }).catch(error => notify(error.message, 'error'));
  }, [notify]);

  async function saveGateway() {
    if (form.provider !== 'razorpay') {
      notify('This release currently implements Razorpay checkout.', 'error');
      return;
    }
    if (!form.keyId.trim()) {
      notify('Enter the Razorpay Key ID.', 'error');
      return;
    }
    if (!configured && (!form.keySecret.trim() || !form.webhookSecret.trim())) {
      notify('Key Secret and Webhook Secret are required for first-time configuration.', 'error');
      return;
    }

    setSaving(true);
    try {
      const privatePayload = {
        provider: 'razorpay',
        enabled: form.enabled,
        mode: form.mode,
        merchantName: form.merchantName.trim() || 'Weilink Signage',
        keyId: form.keyId.trim(),
        updatedAt: serverTimestamp(),
      };
      // Empty secret fields intentionally keep the already-stored secret unchanged.
      if (form.keySecret.trim()) privatePayload.keySecret = form.keySecret.trim();
      if (form.webhookSecret.trim()) privatePayload.webhookSecret = form.webhookSecret.trim();

      await setDoc(doc(db, 'system_private', 'payment_gateway'), privatePayload, { merge: true });
      await setDoc(doc(db, 'system_settings', 'payment_gateway'), {
        provider: 'razorpay',
        enabled: form.enabled,
        mode: form.mode,
        merchantName: form.merchantName.trim() || 'Weilink Signage',
        keyId: form.keyId.trim(),
        configured: true,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      setConfigured(true);
      setForm(prev => ({ ...prev, keySecret: '', webhookSecret: '' }));
      notify('Razorpay payment gateway saved.', 'success');
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function copyWebhook() {
    await navigator.clipboard.writeText(webhookUrl);
    notify('Webhook URL copied.', 'success');
  }

  return <>
    <PageHeader
      eyebrow="Billing infrastructure"
      title="Payment Gateways"
      description="Configure the gateway used when tenants purchase or renew a Weilink Signage package. Secret values are stored in a private Firestore document and are never read back into this page."
      actions={<button className="btn btn-primary" disabled={saving} onClick={saveGateway}><Save size={17}/>{saving ? 'Saving…' : 'Save gateway'}</button>}
    />

    <div className="stats-grid three gateway-stats">
      <Card className="payment-status-card gateway-blue"><div className="gateway-icon"><CreditCard/></div><div><span>Provider</span><strong>Razorpay</strong><small>UPI · Cards · Wallets · Netbanking</small></div></Card>
      <Card className="payment-status-card gateway-green"><div className="gateway-icon"><ShieldCheck/></div><div><span>Configuration</span><strong>{configured ? 'Configured' : 'Not configured'}</strong><small>Server-side payment activation</small></div></Card>
      <Card className="payment-status-card gateway-purple"><div className="gateway-icon"><Webhook/></div><div><span>Mode</span><strong>{form.mode === 'live' ? 'Live' : 'Test'}</strong><small>Switch only after sandbox testing</small></div></Card>
    </div>

    <div className="content-grid two-one">
      <Card className="gateway-config-card">
        <div className="card-head"><div><span className="eyebrow">Gateway credentials</span><h3>Razorpay configuration</h3></div><Badge tone={form.enabled ? 'success' : 'warning'}>{form.enabled ? 'Enabled' : 'Disabled'}</Badge></div>
        <div className="form-grid two-col">
          <label className="field"><span>Gateway provider</span><select value={form.provider} onChange={e=>setForm(f=>({...f,provider:e.target.value}))}><option value="razorpay">Razorpay</option></select></label>
          <label className="field"><span>Environment</span><select value={form.mode} onChange={e=>setForm(f=>({...f,mode:e.target.value}))}><option value="test">Test / Sandbox</option><option value="live">Live</option></select></label>
          <label className="field"><span>Merchant / checkout name</span><input value={form.merchantName} onChange={e=>setForm(f=>({...f,merchantName:e.target.value}))}/></label>
          <label className="field"><span>Key ID</span><input placeholder="rzp_test_... or rzp_live_..." value={form.keyId} onChange={e=>setForm(f=>({...f,keyId:e.target.value}))}/></label>
          <label className="field"><span>Key Secret {configured && <small>(leave blank to keep existing)</small>}</span><div className="secret-input"><input type={showSecret?'text':'password'} value={form.keySecret} placeholder={configured?'••••••••••••••••':'Enter Key Secret'} onChange={e=>setForm(f=>({...f,keySecret:e.target.value}))}/><button type="button" onClick={()=>setShowSecret(v=>!v)}>{showSecret?<EyeOff size={17}/>:<Eye size={17}/>}</button></div></label>
          <label className="field"><span>Webhook Secret {configured && <small>(leave blank to keep existing)</small>}</span><div className="secret-input"><input type={showWebhook?'text':'password'} value={form.webhookSecret} placeholder={configured?'••••••••••••••••':'Create a Razorpay webhook secret'} onChange={e=>setForm(f=>({...f,webhookSecret:e.target.value}))}/><button type="button" onClick={()=>setShowWebhook(v=>!v)}>{showWebhook?<EyeOff size={17}/>:<Eye size={17}/>}</button></div></label>
        </div>
        <label className="toggle-row gateway-enable"><div><strong>Enable tenant online payments</strong><small>When disabled, Buy/Upgrade buttons show that online payment is unavailable.</small></div><input type="checkbox" checked={form.enabled} onChange={e=>setForm(f=>({...f,enabled:e.target.checked}))}/></label>
        <div className="gateway-security-note"><KeyRound size={18}/><div><strong>Secret handling</strong><p>The Key Secret is written to <code>system_private/payment_gateway</code>. Apply the supplied Firestore rules so clients cannot read this document.</p></div></div>
      </Card>

      <Card className="webhook-card">
        <div className="card-head"><div><span className="eyebrow">Razorpay dashboard</span></div><Webhook size={20}/></div>
        {/* <p>After deploying Firebase Functions, add this URL in Razorpay Dashboard → Account & Settings → Webhooks.</p> */}
        {/* <div className="webhook-url"><code>{webhookUrl}</code><button className="icon-btn" onClick={copyWebhook}><Copy size={16}/></button></div> */}
        <div className="gateway-check-list">
          <div><CheckCircle2/> Enable <strong>payment_link.paid</strong></div>
          <div><CheckCircle2/> Enable <strong>payment.failed</strong></div>
          <div><CheckCircle2/> Use the same Webhook Secret entered here</div>
          <div><CheckCircle2/> Test before switching to Live keys</div>
        </div>
      </Card>
    </div>
  </>;
}
